// Purchase controls are owned by Framewerk Notify Me; respect theme-editor block order.
(() => {
  'use strict';
  const text = (key, fallback) => window.HoldcomCROStrings?.[key] || fallback;
  const root = () => window.Shopify?.routes?.root || '/';
  const metadata = new Map();
  const fitments = new Map();
  const requestGroups = new WeakMap();
  const standaloneRequests = new Map();
  const queue = [];
  let activeRequests = 0;
  const MAX_REQUESTS = 6;
  const abortError = () => Object.assign(new Error('Aborted'), { name: 'AbortError' });
  function drain() {
    while (activeRequests < MAX_REQUESTS && queue.length) {
      const task = queue.shift();
      task.signal?.removeEventListener('abort', task.abort);
      if (task.signal?.aborted) { task.reject(abortError()); continue; }
      activeRequests++;
      task.run().then(task.resolve, task.reject).finally(() => { activeRequests--; drain(); });
    }
  }
  function schedule(run, signal) {
    if (signal?.aborted) return Promise.reject(abortError());
    return new Promise((resolve, reject) => {
      const task = { run, signal, resolve, reject, abort: null };
      task.abort = () => { const index = queue.indexOf(task); if (index >= 0) { queue.splice(index, 1); reject(abortError()); } };
      signal?.addEventListener('abort', task.abort, { once: true });
      queue.push(task); drain();
    });
  }
  function remember(cache, key, value, ttl) {
    if (cache.size >= 200) cache.delete(cache.keys().next().value);
    cache.set(key, { value, expires: Date.now() + ttl });
  }
  function cached(cache, key) {
    const entry = cache.get(key);
    if (!entry) return undefined;
    if (entry.expires <= Date.now()) { cache.delete(key); return undefined; }
    return entry.value;
  }
  const normalise = value => String(value || '').trim().toLowerCase();
  let eventVehicle;
  const selectedVehicle = () => {
    if (eventVehicle !== undefined) return eventVehicle;
    const value = window.Framewerk?.vehicle?.get?.() || window.FramewerkMisterFitment?.getSelectedVehicle?.();
    if (value) return { ...value, code: value.code || value.vehicleCode };
    try { const stored = JSON.parse(localStorage.getItem('framewerk:mister-fitment:selected-vehicle') || 'null'); return stored ? { ...stored, code: stored.code || stored.vehicleCode } : null; } catch { return null; }
  };
  const universal = data => (data?.tags || []).some(tag => normalise(tag) === 'universal');
  const availability = (data, variant) => {
    if (!data) return text('check_availability', 'Check availability');
    if ((variant?.available ?? data.available) === false) return text('sold_out', 'Sold out');
    if (data.preorder) return text('preorder', 'Pre-order');
    if (data.onlineOnly) return text('online_only', 'Online only');
    return text('available', 'Available');
  };
  function readJSON(url, signal, accept = 'application/json') {
    if (signal?.aborted) return Promise.reject(abortError());
    let pending = standaloneRequests;
    if (signal) { if (!requestGroups.has(signal)) requestGroups.set(signal, new Map()); pending = requestGroups.get(signal); }
    const key = accept + ':' + url;
    if (pending.has(key)) return pending.get(key);
    const promise = schedule(() => fetchJSON(url, signal, accept), signal).finally(() => pending.delete(key));
    pending.set(key, promise);
    return promise;
  }
  async function fetchJSON(url, signal, accept) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 3000);
    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: accept } });
      if (!response.ok) throw new Error('Unavailable');
      return await response.json();
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  }
  async function productState(handle, signal) {
    if (signal?.aborted) return null;
    const hit = cached(metadata, handle);
    if (hit !== undefined) return hit;
    const url = new URL(`${root()}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('view', 'holdcom-cro');
    url.searchParams.set('preview_theme_id', String(window.Shopify?.theme?.id || '188760490285'));
    try {
      const result = await readJSON(url, signal, 'text/html');
      if (!result?.id || result.handle !== handle) return null;
      remember(metadata, handle, result, 60000);
      return result;
    } catch { return null; }
  }
  async function fitment(id, vehicle, data, signal) {
    if (signal?.aborted) return 'unknown';
    if (!vehicle?.code) return 'unselected';
    if (universal(data)) return 'universal';
    const key = `${id}:${vehicle.code}`;
    const hit = cached(fitments, key);
    if (hit !== undefined) return hit;
    const url = new URL(`${root()}apps/framewerk`, location.origin);
    url.searchParams.set('resource', 'fitment');
    url.searchParams.set('product_id', String(id));
    url.searchParams.set('vehicle_code', vehicle.code);
    try {
      const result = await readJSON(url, signal);
      const status = ['compatible', 'incompatible'].includes(result?.status) ? result.status : 'unknown';
      remember(fitments, key, status, status === 'unknown' ? 5000 : 300000);
      return status;
    } catch { return 'unknown'; }
  }
  const fitmentLabel = status => ({
    compatible: text('compatible', 'Matches selected vehicle'),
    incompatible: text('incompatible', 'Other models — not a match'),
    universal: text('universal', 'Universal item — check dimensions and installation'),
    unknown: text('unknown', 'Fitment not confirmed — check before ordering'),
    unselected: text('check_fitment', 'Select your vehicle to check fitment')
  }[status] || text('unknown', 'Fitment not confirmed'));
  async function enrich(products, signal) {
    const vehicle = selectedVehicle();
    return Promise.all(products.map(async product => {
      const handle = product.handle || new URL(product.url, location.origin).pathname.split('/').pop();
      // Metadata and authoritative fitment start together; neither waits for the other.
      const [data, checkedStatus] = await Promise.all([productState(handle, signal), fitment(product.id, vehicle, null, signal)]);
      const status = vehicle && universal(data) ? 'universal' : checkedStatus;
      return { ...product, _hcState: data, _hcFitment: status, _hcVehicle: vehicle?.code || '' };
    }));
  }
  window.HoldcomCRO = { productState, selectedVehicle, availability, fitmentLabel, enrich, fitment };
  function syncGuides() {
    for (const guide of document.querySelectorAll('[data-hc-buying-guide]')) {
      if (!guide.closest('.product-details')) continue;
      let data;
      try { data = JSON.parse(guide.querySelector('[data-hc-product-state]').textContent); } catch { continue; }
      remember(metadata, data.handle, data, 60000);
    }
  }

  function syncInsets() {
    const lowerNav = document.querySelector('.framewerk-mobile-nav');
    const navRect = lowerNav?.getBoundingClientRect();
    const navVisible = navRect && navRect.height > 0 && navRect.top < innerHeight && getComputedStyle(lowerNav).position === 'fixed';

    const bar = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    const modal = document.querySelector('dialog[open]');
    const height = bar && !modal ? Math.ceil(bar.getBoundingClientRect().height) + 12 : 0;
    const styles = document.documentElement.style;
    const navHeight = `${navVisible ? Math.max(0, innerHeight - navRect.top) : 0}px`;
    if (styles.getPropertyValue('--hc-lower-nav-height') !== navHeight) styles.setProperty('--hc-lower-nav-height', navHeight);
    if (styles.getPropertyValue('--hc-sticky-clearance') !== `${height}px`) styles.setProperty('--hc-sticky-clearance', `${height}px`);
    document.documentElement.classList.toggle('hc-sticky-active', height > 0);
    document.documentElement.classList.toggle('hc-dialog-open', Boolean(modal));
    const viewport = window.visualViewport;
    document.documentElement.classList.toggle('hc-keyboard-open', Boolean(viewport && innerHeight - viewport.height > 160));
  }
  function syncPurchaseLayout() {
    for (const details of document.querySelectorAll('.product-details')) {
      const guide = details.querySelector('[data-hc-buying-guide]');
      details.querySelectorAll('[data-hc-fitment-status]').forEach(note => note.remove());
      if (guide) guide.classList.toggle('hc-guide-empty', !guide.querySelector('details,.hc-buying-guide__critical'));
      const price = details.querySelector('product-price');
      const rewards = details.querySelector('[class*="ll__product-"].ll__container')?.closest('.shopify-app-block');
      if (price && rewards && price.nextElementSibling !== rewards) {
        rewards.classList.add('hc-rewards-below-price');
        price.after(rewards);
      }
      const controls = details.querySelector('.product-form-buttons');
      const quantity = controls?.querySelector('quantity-selector-component');
      const input = quantity?.querySelector('input[name="quantity"]');
      if (input) {
        let trigger = quantity.parentElement.querySelector('.hc-quantity-trigger');
        if (!trigger) {
          trigger = document.createElement('button');
          trigger.type = 'button'; trigger.className = 'hc-quantity-trigger';
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.addEventListener('click', () => openQuantityPicker(quantity, trigger));
          quantity.after(trigger);
        }
        const value = input.value;
        if (trigger.textContent !== value) trigger.textContent = value;
        trigger.setAttribute('aria-label', `Quantity ${value}, choose quantity`);
        trigger.disabled = input.disabled;
      }
      const wish = details.querySelector('.framewerk-wishlist-product-button');
      if (wish) {
        const label = `${wish.getAttribute('aria-pressed') === 'true' ? 'Remove' : 'Add'} ${wish.dataset.productTitle || 'product'} ${wish.getAttribute('aria-pressed') === 'true' ? 'from' : 'to'} wishlist`;
        if (wish.getAttribute('aria-label') !== label) wish.setAttribute('aria-label', label);
      }
      if (controls && wish && !controls.contains(wish)) {
        const former = wish.closest('.shopify-app-block');
        wish.type = 'button';
        controls.append(wish);
        controls.classList.add('hc-purchase-controls');
        if (former && !former.querySelector('button,input,select')) former.classList.add('hc-wishlist-empty');
      }
      // Combined Listings must stay visible; only optional add-ons may collapse.
      for (const disclosure of details.querySelectorAll('details.hc-related-options')) {
        const listings = disclosure.querySelectorAll('.fwpo-listing');
        if (!listings.length) continue;
        for (const listing of listings) disclosure.before(listing);
        disclosure.remove();
      }
    }
  }
  function openQuantityPicker(component, trigger) {
    const source = component.querySelector('input[name="quantity"]');
    if (!source || source.disabled) return;
    const min = Number(source.min) || 1, step = Number(source.step) || 1;
    const max = component.getEffectiveMax?.() ?? (source.max ? Number(source.max) : null);
    const dialog = document.createElement('dialog');
    dialog.className = 'hc-quantity-dialog'; dialog.setAttribute('aria-label', 'Choose quantity');
    const heading = document.createElement('h2'); heading.textContent = 'Choose quantity';
    const wheel = document.createElement('select'); wheel.size = 5; wheel.setAttribute('aria-label', 'Scroll to choose quantity');
    const current = Number(source.value) || min;
    const start = Math.max(min, current > min + 98 * step ? current - 49 * step : min);
    for (let value = start; value <= Math.min(max ?? Infinity, start + 98 * step); value += step) {
      const option = new Option(String(value), String(value)); wheel.add(option);
    }
    wheel.value = String(current);
    const label = document.createElement('label'); label.textContent = 'Or enter quantity';
    const number = document.createElement('input'); number.type = 'number'; number.min = String(min); number.step = String(step);
    if (max !== null) number.max = String(max);
    number.value = String(current); number.setAttribute('aria-label', 'Enter quantity'); label.append(number);
    wheel.addEventListener('change', () => { number.value = wheel.value; });
    const apply = document.createElement('button'); apply.type = 'button'; apply.textContent = 'Use quantity';
    apply.addEventListener('click', () => {
      if (!number.reportValidity() || !number.value || !source.isConnected) return;
      source.value = number.value;
      source.dispatchEvent(new Event('input', { bubbles: true }));
      source.dispatchEvent(new Event('change', { bubbles: true }));
      component.setQuantity({ target: source, preventDefault() {} });
      syncPurchaseLayout(); dialog.close();
    });
    const cancel = document.createElement('button'); cancel.type = 'button'; cancel.textContent = 'Cancel'; cancel.addEventListener('click', () => dialog.close());
    dialog.append(heading, wheel, label, apply, cancel); document.body.append(dialog);
    dialog.addEventListener('click', event => { const r = dialog.getBoundingClientRect(); if (event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) dialog.close(); });
    dialog.addEventListener('close', () => { dialog.remove(); if (trigger.isConnected) trigger.focus(); syncInsets(); }, { once: true });
    dialog.showModal(); wheel.focus(); syncInsets();
  }
  function boot() {
    syncGuides(); syncInsets(); syncPurchaseLayout();
    const main = document.querySelector('main');
    let zoomOpener;
    main?.addEventListener('click', event => {
      if (!matchMedia('(max-width:749px)').matches || event.target.closest('zoom-dialog')) return;
      const slide = event.target.closest('.product-media-container--zoomable');
      if (slide) { zoomOpener = slide; if (!slide.hasAttribute('tabindex')) slide.tabIndex = -1; }
    }, true);
    main?.addEventListener('close', event => {
      if (event.target.closest('zoom-dialog') && zoomOpener?.isConnected) {
        zoomOpener.focus({ preventScroll: true }); zoomOpener = null;
      }
    }, true);
    let purchaseFrame = 0;
    let guidesChanged = false;
    const purchaseTargets = '.product-details, [data-hc-buying-guide], [data-hc-fitment-status], variant-picker, product-price, .ll__container, .product-form-buttons, quantity-selector-component, .framewerk-wishlist-product-button';
    const touchesPurchase = node => node.nodeType === 1 && (node.matches(purchaseTargets) || node.querySelector(purchaseTargets));
    if (main) new MutationObserver(records => {
      const relevant = records.some(record => record.type === 'attributes' ? record.target.matches('.framewerk-wishlist-product-button') : [...record.addedNodes, ...record.removedNodes].some(touchesPurchase));
      if (!relevant) return;
      guidesChanged ||= records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-hc-buying-guide]') || node.querySelector('[data-hc-buying-guide]'))));
      if (purchaseFrame) return;
      purchaseFrame = requestAnimationFrame(() => {
        purchaseFrame = 0;
        syncPurchaseLayout();
        if (guidesChanged) { guidesChanged = false; syncGuides(); }
      });
    }).observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    new MutationObserver(syncInsets).observe(document.body, { attributes: true, attributeFilter: ['data-stuck', 'open'], subtree: true });
    const lowerNav = document.querySelector('.framewerk-mobile-nav');
    if (lowerNav) new ResizeObserver(syncInsets).observe(lowerNav);
    window.visualViewport?.addEventListener('resize', syncInsets);
    window.addEventListener('resize', syncInsets);
    window.addEventListener('pageshow', syncGuides);
    document.addEventListener('variant:update', () => { document.querySelector('.hc-quantity-dialog')?.close(); syncGuides(); syncInsets(); syncPurchaseLayout(); });
    document.addEventListener('quantity-selector:update', syncPurchaseLayout);
    for (const name of ['framewerk:vehicle:changed', 'framewerk:mister-fitment:vehicle-changed']) window.addEventListener(name, event => { eventVehicle = event.detail ? { ...event.detail, code: event.detail.code || event.detail.vehicleCode } : null; syncGuides(); });
    window.addEventListener('framewerk:ready', syncGuides);
    window.Framewerk?.vehicle?.subscribe?.(syncGuides);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();

/* Product carousel arrows follow scroll edges, independently of browsing mode. */
(() => {
  'use strict';
  if (window.__fwProductCarouselArrows) return;
  window.__fwProductCarouselArrows = true;
  const selector = 'slideshow-component.resource-list__carousel:not([infinite])';
  const states = new Map();
  const parts = carousel => ({
    scroller: carousel.querySelector(':scope > slideshow-container > slideshow-slides'),
    previous: carousel.querySelector(':scope > slideshow-container > slideshow-arrows .slideshow-control--previous'),
    next: carousel.querySelector(':scope > slideshow-container > slideshow-arrows .slideshow-control--next'),
  });
  function install(carousel) {
    if (states.has(carousel) || !carousel.querySelector('.product-card')) return;
    const { scroller, previous, next } = parts(carousel);
    if (!scroller || !previous || !next) return;
    let frame = 0;
    function update() {
      frame = 0;
      const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const position = Math.min(maximum, Math.max(0, Math.abs(scroller.scrollLeft)));
      const inactive = !scroller.clientWidth || carousel.getAttribute('disabled') === 'true';
      const atStart = inactive || position <= 1;
      const atEnd = inactive || maximum - position <= 1;
      carousel.toggleAttribute('data-fw-carousel-start', atStart);
      carousel.toggleAttribute('data-fw-carousel-end', atEnd);
      for (const [button, blocked] of [[previous, atStart], [next, atEnd]]) {
        if (button.disabled !== blocked) button.disabled = blocked;
        button.setAttribute('aria-hidden', String(blocked));
      }
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const resize = new ResizeObserver(schedule);
    resize.observe(scroller);
    const observedSlides = new Set();
    const observeSlides = () => {
      const slides = new Set(scroller.querySelectorAll(':scope > slideshow-slide'));
      observedSlides.forEach(slide => { if (!slides.has(slide)) { resize.unobserve(slide); observedSlides.delete(slide); } });
      slides.forEach(slide => { if (!observedSlides.has(slide)) { resize.observe(slide); observedSlides.add(slide); } });
    };
    observeSlides();
    const mutations = new MutationObserver(records => {
      if (records.some(record => record.type === 'childList')) observeSlides();
      schedule();
    });
    // The native slideshow also writes disabled flags from slide indices. Reconcile
    // those writes with the actual pixel edges, including partially swiped slides.
    mutations.observe(carousel, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    scroller.addEventListener('scroll', schedule, { passive: true });
    scroller.addEventListener('scrollend', schedule);
    carousel.setAttribute('data-fw-product-carousel', '');
    states.set(carousel, {
      scroller, previous, next, schedule,
      destroy() {
        cancelAnimationFrame(frame); resize.disconnect(); mutations.disconnect();
        scroller.removeEventListener('scroll', schedule); scroller.removeEventListener('scrollend', schedule);
        carousel.removeAttribute('data-fw-product-carousel');
      },
    });
    update();
  }
  function discover() {
    for (const [carousel, state] of states) {
      const current = parts(carousel);
      if (!carousel.isConnected || current.scroller !== state.scroller || current.previous !== state.previous || current.next !== state.next) {
        state.destroy(); states.delete(carousel);
      }
    }
    document.querySelectorAll(selector).forEach(install);
  }
  function boot() {
    discover();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; discover(); });
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', discover);
    window.addEventListener('pageshow', () => { discover(); states.forEach(state => state.schedule()); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();
